import { useMutation, useMutationState, useQuery } from '@tanstack/react-query';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithStorage } from 'jotai/utils';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Button, toast } from '@gitbook/spine-react';

import {
    GitBookAgentClientStatus,
    getChangeRequestSubject,
    useAgentState,
    useSiteFindingAgent,
} from '../../Components';
import { apiClient, apiQuery, apiQueryClient, apiResource, getAPIQueryKey } from '../../state';
import { shouldWaitForSoftFailureConfirmation } from './triageProcessing';

type LinkedChangeRequest =
    api.OperationSpec<'listChangeRequestsForSiteFinding'>['ResponseBody']['items'][number];

/** The screen a finding was processed from, so toasts link back to where the user started. */
export type FindingScreenType = 'triage' | 'content_gaps' | 'link_audit';

type ProcessingFinding = {
    id: string;
    organizationId: string;
    siteId: string;
    title: string;
    hadLinkedChangeRequestsAtRunStart: boolean;
    agentMessageCountAtRunStart: number;
    startedAt: number;
    screenType?: FindingScreenType;
};

/**
 * Input needed to start and monitor CR creation for a site finding.
 */
export type TriggerChangeRequestsForFindingInput = {
    organizationId: string;
    siteId: string;
    finding: api.SiteFinding;
    hasLinkedChangeRequests: boolean;
    agentMessageCount: number;
    /** @default 'triage' */
    screenType?: FindingScreenType;
};

const TRIGGER_MUTATION_KEY = ['triggerChangeRequests'];
const LINKED_CHANGE_REQUESTS_REFETCH_INTERVAL = 5000;
const PROCESSING_TIMEOUT = 10 * 60 * 1000; // 10 mins
const PROCESSING_FINDING_RETENTION = 1000 * 60 * 60 * 24; // 24 hours

function getProcessingFindingKey(siteId: string, findingId: string) {
    return `${siteId}:${findingId}`;
}

function getActiveProcessingFindings(
    findings: Record<string, ProcessingFinding>,
    now = Date.now()
): Record<string, ProcessingFinding> {
    return Object.fromEntries(
        Object.entries(findings).filter(
            ([, finding]) => now - finding.startedAt < PROCESSING_FINDING_RETENTION
        )
    );
}

const processingFindingsAtom = atomWithStorage<Record<string, ProcessingFinding>>(
    'triageProcessingFindings',
    {},
    undefined,
    { getOnInit: true }
);

const isProcessingFindingSelector = atomFamily((key: string) =>
    atom((get) => key in getActiveProcessingFindings(get(processingFindingsAtom)))
);

const isAnyFindingProcessingForSiteSelector = atomFamily((siteId: string) =>
    atom((get) =>
        Object.values(getActiveProcessingFindings(get(processingFindingsAtom))).some(
            (f) => f.siteId === siteId
        )
    )
);

/**
 * Returns whether a finding is currently tracked as processing.
 */
export function useIsProcessingFinding(siteId: string, findingId: string): boolean {
    return useAtomValue(isProcessingFindingSelector(getProcessingFindingKey(siteId, findingId)));
}

/**
 * Returns whether any finding on a site is currently tracked as processing.
 */
export function useIsAnyFindingProcessingForSite(siteId: string): boolean {
    return useAtomValue(isAnyFindingProcessingForSiteSelector(siteId));
}

/**
 * Returns whether the trigger request is pending for a finding.
 */
export function useIsTriggeringChangeRequestsForFinding(
    siteId: string,
    findingId: string
): boolean {
    const pendingIds = useMutationState({
        filters: { mutationKey: TRIGGER_MUTATION_KEY, status: 'pending' },
        select: (m) => {
            const variables = m.state.variables as TriggerChangeRequestsForFindingInput;
            return getProcessingFindingKey(variables.siteId, variables.finding.id);
        },
    });
    return pendingIds.includes(getProcessingFindingKey(siteId, findingId));
}

/**
 * Returns a callback that starts CR creation for a finding.
 */
export function useTriggerChangeRequestsForFinding(): (
    input: TriggerChangeRequestsForFindingInput
) => void {
    const processingFindings = useAtomValue(processingFindingsAtom);
    const setProcessingFindings = useSetAtom(processingFindingsAtom);
    const checkIsAnyProcessingForSite = React.useCallback(
        (siteId: string) =>
            Object.values(getActiveProcessingFindings(processingFindings)).some(
                (f) => f.siteId === siteId
            ),
        [processingFindings]
    );

    const { mutate } = useMutation({
        mutationKey: TRIGGER_MUTATION_KEY,
        mutationFn: async (input: TriggerChangeRequestsForFindingInput) => {
            await apiClient.siteContext.triggerChangeRequestsForSiteFinding(
                input.organizationId,
                input.siteId,
                input.finding.id
            );
        },
        onMutate: ({
            organizationId,
            siteId,
            finding,
            hasLinkedChangeRequests,
            agentMessageCount,
            screenType,
        }) => {
            setProcessingFindings((current) => {
                const activeFindings = getActiveProcessingFindings(current);
                if (Object.values(activeFindings).some((f) => f.siteId === siteId)) {
                    return current;
                }
                return {
                    ...activeFindings,
                    [getProcessingFindingKey(siteId, finding.id)]: {
                        id: finding.id,
                        organizationId,
                        siteId,
                        title: finding.title,
                        hadLinkedChangeRequestsAtRunStart: hasLinkedChangeRequests,
                        agentMessageCountAtRunStart: agentMessageCount,
                        startedAt: Date.now(),
                        screenType,
                    },
                };
            });
        },
        onSuccess: () => {
            toast.info(
                'GitBook Agent is creating and implementing change requests in the background.'
            );
        },
        onError: (error, { organizationId, siteId, finding, screenType }) => {
            setProcessingFindings((current) => {
                const key = getProcessingFindingKey(siteId, finding.id);
                if (!(key in current)) return current;
                const { [key]: _removed, ...rest } = current;
                return rest;
            });
            toast.error("Couldn't create change requests", error instanceof Error ? error : null, {
                description: (
                    <ToastDescriptionWithAction
                        description={finding.title}
                        action={
                            <Button
                                kind="secondary"
                                size="small"
                                label="View issue"
                                trailingIcon={Icon.ArrowRight}
                                href={getFindingScreenTarget({
                                    screenType,
                                    organizationId,
                                    siteId,
                                    findingId: finding.id,
                                })}
                            />
                        }
                    />
                ),
            });
        },
    });

    return React.useCallback(
        (input: TriggerChangeRequestsForFindingInput) => {
            if (checkIsAnyProcessingForSite(input.siteId)) {
                return;
            }
            mutate(input);
        },
        [mutate, checkIsAnyProcessingForSite]
    );
}

/**
 * Renderless component that monitors active triage processing findings and emits toasts.
 * Mount once at the app root so toasts survive navigation.
 */
export function TriageProcessingToasts() {
    const processingFindings = useAtomValue(processingFindingsAtom);
    const setProcessingFindings = useSetAtom(processingFindingsAtom);
    const activeProcessingFindings = React.useMemo(
        () => getActiveProcessingFindings(processingFindings),
        [processingFindings]
    );

    React.useEffect(() => {
        if (
            Object.keys(activeProcessingFindings).length !== Object.keys(processingFindings).length
        ) {
            setProcessingFindings(activeProcessingFindings);
        }
    }, [activeProcessingFindings, processingFindings, setProcessingFindings]);

    const handleCompleted = React.useCallback(
        async (finding: ProcessingFinding, changeRequests: LinkedChangeRequest[]) => {
            await Promise.all([
                apiQueryClient.invalidateQueries({
                    queryKey: getAPIQueryKey(
                        apiResource('listSiteFindings', {
                            organizationId: finding.organizationId,
                            siteId: finding.siteId,
                        })
                    ),
                    exact: false,
                }),
                apiQueryClient.invalidateQueries({
                    queryKey: getAPIQueryKey(
                        apiResource('getSiteFindingById', {
                            organizationId: finding.organizationId,
                            siteId: finding.siteId,
                            siteFindingId: finding.id,
                        })
                    ),
                    exact: false,
                }),
            ]);

            setProcessingFindings((current) => {
                const key = getProcessingFindingKey(finding.siteId, finding.id);
                if (!(key in current)) return current;
                const { [key]: _removed, ...rest } = current;
                return rest;
            });

            if (!finding.hadLinkedChangeRequestsAtRunStart) {
                showChangeRequestsCreatedToast({
                    organizationId: finding.organizationId,
                    siteId: finding.siteId,
                    findingId: finding.id,
                    screenType: finding.screenType,
                    changeRequests,
                });
            }
        },
        [setProcessingFindings]
    );

    const handleFailed = React.useCallback(
        (finding: ProcessingFinding) => {
            setProcessingFindings((current) => {
                const key = getProcessingFindingKey(finding.siteId, finding.id);
                if (!(key in current)) return current;
                const { [key]: _removed, ...rest } = current;
                return rest;
            });
            showChangeRequestsFailedToast(finding);
        },
        [setProcessingFindings]
    );

    return (
        <>
            {Object.values(activeProcessingFindings).map((pf) => (
                <TriageProcessingMonitor
                    key={getProcessingFindingKey(pf.siteId, pf.id)}
                    processingFinding={pf}
                    onCompleted={handleCompleted}
                    onFailed={handleFailed}
                />
            ))}
        </>
    );
}

function TriageProcessingMonitor(props: {
    processingFinding: ProcessingFinding;
    onCompleted: (finding: ProcessingFinding, changeRequests: LinkedChangeRequest[]) => void;
    onFailed: (finding: ProcessingFinding) => void;
}) {
    const { processingFinding, onCompleted, onFailed } = props;
    const [hasTimedOut, setHasTimedOut] = React.useState(false);
    const handledRef = React.useRef(false);

    const changeRequestsQuery = useQuery({
        ...apiQuery(
            apiResource('listChangeRequestsForSiteFinding', {
                organizationId: processingFinding.organizationId,
                siteId: processingFinding.siteId,
                siteFindingId: processingFinding.id,
                limit: 10,
            })
        ),
        refetchInterval: LINKED_CHANGE_REQUESTS_REFETCH_INTERVAL,
        refetchIntervalInBackground: true,
    });
    const changeRequests = changeRequestsQuery.data?.items ?? [];

    const agent = useSiteFindingAgent({
        organizationId: processingFinding.organizationId,
        siteId: processingFinding.siteId,
        siteFindingId: processingFinding.id,
    });
    const agentState = useAgentState(agent);
    const hasHydratedAgentState = agentState.status !== GitBookAgentClientStatus.CONNECTING;
    // Records when we requested the fresh zero-CR confirmation fetch for soft failure.
    const settledRefetchAtRef = React.useRef<number | null>(null);

    React.useEffect(() => {
        const remainingTime = Math.max(
            0,
            processingFinding.startedAt + PROCESSING_TIMEOUT - Date.now()
        );
        const timeout = window.setTimeout(() => {
            setHasTimedOut(true);
        }, remainingTime);

        return () => {
            window.clearTimeout(timeout);
        };
    }, [processingFinding.startedAt]);

    React.useEffect(() => {
        if (handledRef.current) return;

        if (
            shouldWaitForSoftFailureConfirmation({
                agentStatus: agentState.status,
                currentAgentMessageCount: agentState.messages.length,
                agentMessageCountAtRunStart: processingFinding.agentMessageCountAtRunStart,
                linkedChangeRequestCount: changeRequests.length,
                linkedChangeRequestsDataUpdatedAt: changeRequestsQuery.dataUpdatedAt,
                isFetchingLinkedChangeRequests: changeRequestsQuery.isFetching,
                softFailureCheckStartedAt: settledRefetchAtRef.current,
            })
        ) {
            if (settledRefetchAtRef.current === null) {
                settledRefetchAtRef.current = Date.now();
                void changeRequestsQuery.refetch();
            }
            return;
        }

        if (changeRequests.length > 0) {
            handledRef.current = true;
            onCompleted(processingFinding, changeRequests);
            return;
        }

        const hasFailed =
            (hasHydratedAgentState &&
                (agentState.status === GitBookAgentClientStatus.ERRORED ||
                    // A refused connection never reopens on its own, so waiting for this run to
                    // report anything else only spends the timeout.
                    agentState.status === GitBookAgentClientStatus.REJECTED)) ||
            (agentState.status === GitBookAgentClientStatus.IDLE &&
                settledRefetchAtRef.current !== null &&
                agentState.messages.length > processingFinding.agentMessageCountAtRunStart) ||
            hasTimedOut;

        if (hasFailed) {
            handledRef.current = true;
            onFailed(processingFinding);
        }
    }, [
        agentState.messages.length,
        agentState.status,
        changeRequests,
        changeRequestsQuery.dataUpdatedAt,
        changeRequestsQuery.isFetching,
        hasHydratedAgentState,
        hasTimedOut,
        onCompleted,
        onFailed,
        processingFinding,
    ]);

    return null;
}

function showChangeRequestsCreatedToast(input: {
    organizationId: string;
    siteId: string;
    findingId: string;
    screenType: FindingScreenType | undefined;
    changeRequests: LinkedChangeRequest[];
}) {
    const { organizationId, siteId, findingId, screenType, changeRequests } = input;
    const findingTarget = getFindingScreenTarget({
        screenType,
        organizationId,
        siteId,
        findingId,
    });

    if (changeRequests.length === 1) {
        const [changeRequest] = changeRequests;
        toast.success('Change request created', {
            description: (
                <ToastDescriptionWithAction
                    description={getChangeRequestSubject(changeRequest)}
                    action={
                        <Button
                            kind="secondary"
                            size="small"
                            label="View change request"
                            trailingIcon={Icon.ArrowRight}
                            href={{
                                screen: {
                                    type: 'space',
                                    organization: organizationId,
                                    space: changeRequest.space,
                                    changeRequest: changeRequest.number,
                                },
                            }}
                        />
                    }
                />
            ),
        });
        return;
    }

    toast.success('Change requests created', {
        description: (
            <ToastDescriptionWithAction
                description={`${changeRequests.length} change requests created to fix this finding`}
                action={
                    <Button
                        kind="secondary"
                        size="small"
                        label="View issue"
                        trailingIcon={Icon.ArrowRight}
                        href={findingTarget}
                    />
                }
            />
        ),
    });
}

function showChangeRequestsFailedToast(finding: ProcessingFinding) {
    toast.error("Couldn't create change requests", null, {
        description: (
            <ToastDescriptionWithAction
                description={finding.title}
                action={
                    <Button
                        kind="secondary"
                        size="small"
                        label="View issue"
                        trailingIcon={Icon.ArrowRight}
                        href={getFindingScreenTarget({
                            screenType: finding.screenType,
                            organizationId: finding.organizationId,
                            siteId: finding.siteId,
                            findingId: finding.id,
                        })}
                    />
                }
            />
        ),
    });
}

function getFindingScreenTarget(input: {
    screenType: FindingScreenType | undefined;
    organizationId: string;
    siteId: string;
    findingId: string;
}) {
    const { screenType = 'triage', organizationId, siteId, findingId } = input;

    return {
        screen: {
            type: screenType,
            organization: organizationId,
            site: siteId,
            finding: findingId,
        },
    };
}

function ToastDescriptionWithAction(props: {
    description: React.ReactNode;
    action: React.ReactNode;
}) {
    const { description, action } = props;

    return (
        <div className="flex min-w-0 flex-col items-start gap-3">
            <div className="max-w-full min-w-0 break-words">{description}</div>
            <div className="flex w-full">{action}</div>
        </div>
    );
}
